import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Bus, Clock, Users, Check, Plus, X, Search, PlayCircle, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isNative, nativeLogin } from "@/lib/nativeAuth";

// jeeo brand palette (charcoal + yellow + cyan) — used only on this pre-login page.
const C = {
  base: "#2F333A", surface: "#383C43", input: "#23262C",
  yellow: "#FFCE00", cyan: "#29C3DB", gray: "#D6D8DB", white: "#F9FAFB", dark: "#1F2227",
};
const TAGS = ["your day", "your crew", "your campus"];

export default function Login() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [demoBusy, setDemoBusy] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem("routerev_school") || "");
  const [reqOpen, setReqOpen] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqNote, setReqNote] = useState("");
  const [reqBusy, setReqBusy] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [uniList, setUniList] = useState(null);
  const [hi, setHi] = useState(-1);
  const [eduBlock, setEduBlock] = useState(() => {
    try { return localStorage.getItem("jeeo_edu_block"); } catch { return null; }
  });
  const mouseMoved = useRef(false);

  const dismissEduBlock = () => {
    try { localStorage.removeItem("jeeo_edu_block"); } catch {}
    setEduBlock(null);
  };

  const loadUnis = () => {
    if (uniList === null) {
      setUniList([]);
      fetch("/us_universities.json").then((r) => r.json()).then(setUniList).catch(() => setUniList([]));
    }
  };

  useEffect(() => {
    if (!reqOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setReqOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reqOpen]);

  const submitRequest = async () => {
    if (!reqName.trim() || !reqEmail.trim()) {
      toast.error("Add your school name and school email.");
      return;
    }
    setReqBusy(true);
    try {
      await api.post("/campus-requests", { school_name: reqName.trim(), email: reqEmail.trim(), note: reqNote.trim() });
      toast.success(`Thanks! We'll add ${reqName.trim()} soon and email you when it's live.`);
      setReqOpen(false);
      setReqName(""); setReqEmail(""); setReqNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request.");
    } finally {
      setReqBusy(false);
    }
  };

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Load schools for the search picker only — no campus theme is applied on the
  // sign-on page. School colors/greeting/tagline kick in AFTER login (AuthContext).
  useEffect(() => {
    api.get("/schools").then((r) => {
      setSchools(r.data);
      const stored = localStorage.getItem("routerev_school");
      if (stored && r.data.find((s) => s.id === stored)) setSelectedId(stored);
    });
  }, []);

  const q = searchQ.trim().toLowerCase();
  // Don't pre-list live campuses — students search to find theirs (the list grows over time).
  const visible = q ? schools.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.city || "").toLowerCase().includes(q)) : [];

  const onboardedNames = new Set(schools.map((s) => (s.name || "").toLowerCase()));
  const uniMatches = (q.length >= 2 && uniList && uniList.length)
    ? uniList
        .filter((u) => !onboardedNames.has(u.n.toLowerCase()) && u.n.toLowerCase().includes(q))
        .sort((a, b) => {
          const ai = a.n.toLowerCase().startsWith(q) ? 0 : 1;
          const bi = b.n.toLowerCase().startsWith(q) ? 0 : 1;
          return ai - bi || a.n.length - b.n.length;
        })
        .slice(0, 8)
    : [];

  const pick = (s) => {
    setSelectedId(s.id);
    localStorage.setItem("routerev_school", s.id);
  };
  const previewSchool = schools.find((s) => s.id === selectedId);

  const requestUni = (u) => {
    setReqName(u.n);
    setReqEmail("");
    setReqOpen(true);
  };

  const navOptions = [
    ...visible.map((s) => ({ t: "s", v: s })),
    ...uniMatches.map((u) => ({ t: "u", v: u })),
  ];
  const activeDesc = hi >= 0 && navOptions[hi]
    ? (navOptions[hi].t === "s" ? `school-option-${navOptions[hi].v.id}` : `uni-suggestion-${hi - visible.length}`)
    : undefined;
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); mouseMoved.current = false; setHi((h) => Math.min(h + 1, navOptions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); mouseMoved.current = false; setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0 && navOptions[hi]) {
      e.preventDefault();
      const o = navOptions[hi];
      o.t === "s" ? pick(o.v) : requestUni(o.v);
    } else if (e.key === "Escape") { setHi(-1); }
  };

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNative()) { nativeLogin(); return; }
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exploreDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/auth/demo");
      localStorage.removeItem("routerev_school");
      await checkAuth();
      navigate("/", { replace: true });
    } catch {
      toast.error("Could not start the demo. Please try again.");
      setDemoBusy(false);
    }
  };

  const FEATURES = [
    { icon: Bus, t: "Live route timing" },
    { icon: Clock, t: "Gap-time finder" },
    { icon: MapPin, t: "Campus map" },
    { icon: Users, t: "Meet up with friends" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-jeeo-body" style={{ background: C.base, color: C.white }}>
      {/* Brand / hero side (jeeo identity — no campus theme) */}
      <div className="relative hidden lg:flex flex-col justify-center px-16 xl:px-24 overflow-hidden" style={{ background: C.base }}>
        {/* soft radial glow + faint map-pin motif for depth */}
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(600px 400px at 20% 25%, ${C.cyan}14, transparent 70%), radial-gradient(500px 380px at 80% 80%, ${C.yellow}10, transparent 70%)` }} />
        <svg className="pointer-events-none absolute -right-16 -bottom-10 opacity-[0.06]" width="460" height="460" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth="1">
          <path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11Z" />
          <circle cx="12" cy="10" r="3" />
        </svg>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
          <img src="/jeeo-logo-trans.png" alt="jeeo" className="h-36 xl:h-44 w-auto mb-8" />
          <div className="mono text-[11px] tracking-[0.35em] uppercase mb-4" style={{ color: C.cyan }}>Welcome to jeeo</div>
          <h1 className="font-jeeo font-semibold leading-[1.05] tracking-tight text-5xl xl:text-6xl max-w-xl" style={{ color: C.white }}>
            Your campus,<br />minus the <span style={{ color: C.yellow }}>guesswork.</span>
          </h1>
          <p className="mt-6 text-lg max-w-md" style={{ color: C.gray }}>
            Map your class routes, catch the right bus, and turn dead time into lunch, study, or a gym session.
          </p>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            {TAGS.map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                {i > 0 && <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.yellow }} />}
                <span className="font-jeeo font-medium text-lg" style={{ color: C.cyan }}>{t}</span>
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Auth / actions side */}
      <div className="relative flex flex-col justify-center items-center px-5 py-10 sm:px-10" style={{ background: C.surface }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="w-full max-w-md"
        >
          {/* Logo + tagline (also carries jeeo brand on mobile where the hero is hidden) */}
          <div className="lg:hidden flex flex-col items-center text-center mb-8">
            <img src="/jeeo-logo-trans.png" alt="jeeo" data-testid="jeeo-logo" className="h-28 w-auto" />
            <div className="mt-3 flex items-center gap-2.5">
              {TAGS.map((t, i) => (
                <span key={t} className="flex items-center gap-2.5">
                  {i > 0 && <span className="w-1 h-1 rounded-full" style={{ background: C.yellow }} />}
                  <span className="font-jeeo font-medium text-sm" style={{ color: C.cyan }}>{t}</span>
                </span>
              ))}
            </div>
          </div>

          <div
            className="rounded-3xl p-6 sm:p-8 shadow-2xl border"
            style={{ background: C.base, borderColor: "rgba(255,255,255,0.08)" }}
          >
            {/* Campus search */}
            <div data-testid="school-selector" onMouseMove={() => (mouseMoved.current = true)}>
              <div className="mono text-[11px] tracking-[0.25em] uppercase mb-2.5" style={{ color: C.cyan }}>Is your campus on jeeo?</div>
              <div className="relative">
                <Search size={16} strokeWidth={1.8} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: C.gray }} />
                <input
                  data-testid="campus-search"
                  role="combobox"
                  aria-expanded={navOptions.length > 0}
                  aria-controls="school-selector"
                  aria-autocomplete="list"
                  aria-activedescendant={activeDesc}
                  value={searchQ}
                  onFocus={loadUnis}
                  onChange={(e) => { loadUnis(); mouseMoved.current = false; setHi(-1); setSearchQ(e.target.value); }}
                  onKeyDown={onSearchKey}
                  placeholder="Type your university…"
                  className="w-full rounded-xl pl-10 pr-3 py-3 text-sm outline-none transition-[border-color,box-shadow] focus:ring-2"
                  style={{ background: C.input, color: C.white, border: "1px solid rgba(255,255,255,0.1)", "--tw-ring-color": C.yellow }}
                />
              </div>

              {visible.length > 0 && (
                <div className="grid gap-2 mt-3">
                  {visible.map((s, idx) => {
                    const active = s.id === selectedId;
                    const hl = hi === idx;
                    return (
                      <button
                        key={s.id}
                        data-testid={`school-option-${s.id}`}
                        role="option"
                        aria-selected={active || hl}
                        onMouseEnter={() => { if (mouseMoved.current) setHi(idx); }}
                        onClick={() => pick(s)}
                        className="flex items-center justify-between rounded-xl px-4 py-3 text-left transition-colors"
                        style={{ border: `1px solid ${active ? (s.theme?.primary || C.yellow) : hl ? C.yellow : "rgba(255,255,255,0.1)"}`, background: active ? `${s.theme?.primary || C.yellow}1f` : hl ? "rgba(255,206,0,0.08)" : "transparent" }}
                      >
                        <span className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold mono" style={{ background: s.theme?.primary || C.cyan }}>
                            {s.short}
                          </span>
                          <span>
                            <span className="block font-semibold text-sm leading-tight" style={{ color: C.white }}>{s.name}</span>
                            <span className="block text-xs" style={{ color: C.gray }}>{s.city}</span>
                          </span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          {active && <Check size={16} strokeWidth={2.5} style={{ color: s.theme?.primary || C.yellow }} />}
                          <span data-testid={`campus-status-${s.id}`}
                            className="text-[11px] mono font-bold uppercase tracking-widest rounded px-1.5 py-0.5"
                            style={{ background: "rgba(34,197,94,0.18)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.3)" }}>
                            Live
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {uniMatches.length > 0 && (
                <div data-testid="uni-suggestions" className="grid gap-1.5 mt-2">
                  <div className="mono text-[11px] tracking-[0.2em] uppercase px-1" style={{ color: C.gray }}>
                    {visible.length > 0 ? "Other universities" : "Not on jeeo yet · request to add"}
                  </div>
                  {uniMatches.map((u, i) => {
                    const hl = hi === visible.length + i;
                    return (
                      <button
                        key={u.n + i}
                        data-testid={`uni-suggestion-${i}`}
                        role="option"
                        aria-selected={hl}
                        onMouseEnter={() => { if (mouseMoved.current) setHi(visible.length + i); }}
                        onClick={() => requestUni(u)}
                        className="flex items-center justify-between rounded-xl px-4 py-2.5 text-left transition-colors"
                        style={{ border: `1px solid ${hl ? C.cyan : "rgba(255,255,255,0.1)"}`, background: hl ? "rgba(41,195,219,0.08)" : "transparent" }}
                      >
                        <span className="text-sm truncate pr-2" style={{ color: C.white }}>{u.n}</span>
                        <span className="shrink-0 text-[11px] mono font-bold uppercase tracking-widest rounded px-1.5 py-0.5" style={{ background: "rgba(255,206,0,0.15)", color: C.yellow }}>request</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {q && visible.length === 0 && uniMatches.length === 0 && (
                <button
                  data-testid="search-request-cta"
                  onClick={() => { setReqName(searchQ.trim()); setReqOpen(true); }}
                  className="mt-2 w-full text-left rounded-xl px-4 py-3 text-sm transition-colors"
                  style={{ border: `1px dashed ${C.cyan}`, background: "rgba(41,195,219,0.06)", color: C.white }}
                >
                  <span className="font-semibold">"{searchQ.trim()}"</span> isn't on jeeo yet — <span className="font-semibold" style={{ color: C.cyan }}>request it →</span>
                </button>
              )}

              <button
                data-testid="request-campus-link"
                onClick={() => { setReqName(""); setReqEmail(""); setReqNote(""); setReqOpen(true); }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
                style={{ color: C.cyan }}
              >
                <Plus size={13} strokeWidth={2.5} /> Don't see your campus? Request it
              </button>
            </div>

            {previewSchool && (
              <div data-testid="brand-preview" className="mt-4 rounded-xl overflow-hidden border" style={{ borderColor: `${previewSchool.theme?.primary || C.cyan}55` }}>
                <div className="h-1.5 w-full" style={{ background: previewSchool.theme?.primary || C.cyan }} />
                <div className="flex items-center gap-3 p-3" style={{ background: `${previewSchool.theme?.primary || C.cyan}14` }}>
                  <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold mono shrink-0" style={{ background: previewSchool.theme?.primary || C.cyan }}>
                    {previewSchool.short}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold" style={{ color: C.white }}>
                      {previewSchool.name} is live 🎉
                    </div>
                    <div className="text-xs" style={{ color: C.gray }}>
                      Sign in to unlock your campus — “{previewSchool.theme?.greeting || "Howdy"}!”
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="my-6 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />

            {/* Sign in */}
            <button
              data-testid="google-login-btn"
              onClick={handleLogin}
              className="w-full flex items-center justify-center gap-3 rounded-xl py-3.5 font-bold text-base transition-[background-color,transform] hover:-translate-y-0.5 active:scale-[0.99] focus:ring-2 focus:ring-offset-2"
              style={{ background: C.yellow, color: C.dark, "--tw-ring-color": C.yellow, "--tw-ring-offset-color": C.base }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#E6B800")}
              onMouseLeave={(e) => (e.currentTarget.style.background = C.yellow)}
            >
              <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
              <span className="mono text-[11px] tracking-[0.2em] uppercase" style={{ color: C.gray }}>or</span>
              <div className="h-px flex-1" style={{ background: "rgba(255,255,255,0.08)" }} />
            </div>

            <button
              data-testid="explore-demo-btn"
              onClick={exploreDemo}
              disabled={demoBusy}
              className="w-full flex items-center justify-center gap-2.5 rounded-xl py-3.5 font-semibold transition-colors hover:-translate-y-0.5 disabled:opacity-60"
              style={{ border: `2px solid ${C.cyan}`, color: C.cyan, background: "transparent" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(41,195,219,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <PlayCircle size={18} strokeWidth={1.8} />
              {demoBusy ? "Loading demo…" : "Explore the demo"}
            </button>
            <p className="text-xs text-center mt-2" style={{ color: C.gray }}>
              No sign-in needed — a fully-loaded sample campus.
            </p>
          </div>

          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-3 mt-8">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" style={{ color: C.gray }}>
                <f.icon size={16} strokeWidth={1.8} style={{ color: i % 2 === 0 ? C.yellow : C.cyan }} />
                {f.t}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Request-campus modal */}
      <AnimatePresence>
        {reqOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 font-jeeo-body"
            onClick={() => setReqOpen(false)}
            data-testid="request-campus-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden border"
              style={{ background: C.surface, borderColor: "rgba(255,255,255,0.1)", color: C.white }}
            >
              <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div>
                  <div className="mono text-[11px] tracking-[0.2em] uppercase" style={{ color: C.cyan }}>Request a campus</div>
                  <h3 className="font-jeeo text-lg font-semibold">Add your school</h3>
                </div>
                <button data-testid="request-campus-close" onClick={() => setReqOpen(false)}
                  className="p-2 rounded-md transition-colors hover:bg-white/10">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm" style={{ color: C.gray }}>
                  Tell us your school and we'll build your campus (buildings, dining, bus routes) and email you when it's live.
                </p>
                {[
                  { k: "name", tid: "request-school-name", label: "School name", ph: "e.g. University of Houston", val: reqName, set: setReqName, type: "text", el: "input" },
                  { k: "email", tid: "request-school-email", label: "Your school email (.edu)", ph: "you@school.edu", val: reqEmail, set: setReqEmail, type: "email", el: "input" },
                  { k: "note", tid: "request-school-note", label: "Anything else? (optional)", ph: "Main bus line, campus quirks, etc.", val: reqNote, set: setReqNote, el: "textarea" },
                ].map((f) => (
                  <label key={f.k} className="block">
                    <span className="mono text-[11px] tracking-[0.2em] uppercase" style={{ color: C.gray }}>{f.label}</span>
                    {f.el === "textarea" ? (
                      <textarea data-testid={f.tid} value={f.val} onChange={(e) => f.set(e.target.value)} placeholder={f.ph}
                        className="mt-1 w-full h-20 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 resize-none"
                        style={{ background: C.input, color: C.white, border: "1px solid rgba(255,255,255,0.1)", "--tw-ring-color": C.yellow }} />
                    ) : (
                      <input data-testid={f.tid} type={f.type} value={f.val} onChange={(e) => f.set(e.target.value)} placeholder={f.ph}
                        className="mt-1 w-full rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2"
                        style={{ background: C.input, color: C.white, border: "1px solid rgba(255,255,255,0.1)", "--tw-ring-color": C.yellow }} />
                    )}
                  </label>
                ))}
                <button data-testid="request-campus-submit" onClick={submitRequest} disabled={reqBusy}
                  className="w-full rounded-xl py-3 text-sm font-bold transition-colors disabled:opacity-50"
                  style={{ background: C.yellow, color: C.dark }}>
                  {reqBusy ? "Sending…" : "Request my campus"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* .edu-only sign-in nudge (shown when a non-university Google account was used) */}
      <AnimatePresence>
        {eduBlock && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 font-jeeo-body"
            onClick={() => dismissEduBlock()}
            data-testid="edu-gate-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden border"
              style={{ background: C.surface, borderColor: "rgba(255,255,255,0.1)", color: C.white }}
            >
              <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(41,195,219,0.15)" }}>
                    <GraduationCap size={18} strokeWidth={1.8} style={{ color: C.cyan }} />
                  </span>
                  <div>
                    <div className="mono text-[11px] tracking-[0.2em] uppercase" style={{ color: C.cyan }}>Students only</div>
                    <h3 className="font-jeeo text-lg font-semibold">Use your school email</h3>
                  </div>
                </div>
                <button data-testid="edu-gate-close" onClick={() => dismissEduBlock()}
                  className="p-2 rounded-md transition-colors hover:bg-white/10">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-sm" style={{ color: C.gray }}>
                  jeeo is built for students, so we sign you in with your{" "}
                  <span className="font-semibold" style={{ color: C.white }}>university (.edu) Google account</span>.
                  {eduBlock !== "1" && (
                    <> The account <span className="font-semibold" style={{ color: C.white }}>{eduBlock}</span> isn't a school address.</>
                  )}
                </p>
                <p className="text-sm" style={{ color: C.gray }}>
                  Tap continue, then choose <span className="font-semibold" style={{ color: C.white }}>"Use another account"</span> and pick your <span className="mono">@yourschool.edu</span> Google account.
                </p>
                <button data-testid="edu-gate-retry" onClick={handleLogin}
                  className="w-full flex items-center justify-center gap-3 rounded-xl py-3.5 font-bold transition-transform hover:-translate-y-0.5"
                  style={{ background: C.yellow, color: C.dark }}>
                  <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
                  Continue with school email
                </button>
                <button data-testid="edu-gate-request" onClick={() => { dismissEduBlock(); setReqName(""); setReqEmail(""); setReqNote(""); setReqOpen(true); }}
                  className="w-full text-center text-xs font-semibold hover:underline" style={{ color: C.cyan }}>
                  My school isn't on jeeo yet — request it
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
